import { AlarmClock } from '@/components/ui/icons';
import { useState } from 'react';
import type { MessageDTO } from '@clanfire/shared';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { formatReminder, setReminder } from '@/lib/saved';
import { cn } from '@/lib/utils';
import { useSavedState } from '@/stores/saved';

const pad = (n: number) => String(n).padStart(2, '0');
const localValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Tomorrow (or Monday, for "next week") at 9:00 in the morning. */
function morning(daysAhead: number, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + daysAhead);
  d.setHours(9, 0, 0, 0);
  return d;
}
function nextMonday(now = new Date()) {
  const days = ((8 - now.getDay()) % 7) || 7;
  return morning(days, now);
}

/** Saves a message with a reminder: a notification at the time you pick. */
export function RemindMeModal({ message, onClose }: { message: MessageDTO; onClose: () => void }) {
  const current = useSavedState(message.id);
  const now = new Date();
  const choices: Array<[string, Date]> = [
    ['In 20 minutes', new Date(now.getTime() + 20 * 60_000)],
    ['In 1 hour', new Date(now.getTime() + 60 * 60_000)],
    ['In 3 hours', new Date(now.getTime() + 3 * 60 * 60_000)],
    ['Tomorrow morning', morning(1)],
    ['Next week', nextMonday()],
  ];
  const [custom, setCustom] = useState(localValue(new Date(now.getTime() + 24 * 60 * 60_000)));
  const [busy, setBusy] = useState<string | null>(null);

  const pick = async (key: string, at: Date | null) => {
    setBusy(key);
    if (await setReminder(message, at)) onClose();
    else setBusy(null);
  };

  return (
    <Modal open onClose={onClose} title="Remind me" description="The message is saved, and you get a notification at the time you pick." size="sm">
      {current && (
        <p className="mb-3 flex items-center gap-2 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-ink">
          <AlarmClock size={15} className="shrink-0" /> Set for {formatReminder(current)}
          <span className="flex-1" />
          <Button size="sm" variant="link" loading={busy === 'clear'} onClick={() => void pick('clear', null)}>
            Remove
          </Button>
        </p>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {choices.map(([label, at]) => (
          <button
            key={label}
            type="button"
            disabled={!!busy}
            onClick={() => void pick(label, at)}
            className={cn('flex flex-col items-start rounded-xl border border-line px-3 py-2 text-left transition-colors hover:border-accent hover:bg-accent-soft disabled:opacity-60')}
          >
            <span className="text-sm font-semibold text-fg">{label}</span>
            <span className="text-xs text-fg-muted">{formatReminder(at.toISOString())}</span>
          </button>
        ))}
      </div>
      <form
        className="mt-4 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const at = new Date(custom);
          if (!Number.isNaN(at.getTime())) void pick('custom', at);
        }}
      >
        <div className="flex-1">
          <Input label="Or pick a time" type="datetime-local" value={custom} min={localValue(now)} onChange={(e) => setCustom(e.target.value)} />
        </div>
        <Button type="submit" loading={busy === 'custom'}>
          Set
        </Button>
      </form>
    </Modal>
  );
}
